import { useState } from "react";
import { apiRequest } from "../../../shared/api/client";
import type { SessionUser } from "../../../shared/auth/access";
import type { Task, TaskStatus, TaskAssignee } from "./warehouse-types";
import { nextActions, exceptionStatuses } from "./warehouse-presentation";
export type WarehouseTaskProps = {
  task: Task; user: SessionUser; notify: (message: string) => void;
  onSaved: (task: Task) => void; onRefresh: () => Promise<void>;
};
export function useWarehouseTaskWorkspace({ task, user, notify, onSaved, onRefresh }: WarehouseTaskProps) {
  const [quantity, setQuantity] = useState(
    String(task.preparedQuantity || task.receivedQuantity || ""),
  );
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignees, setAssignees] = useState<TaskAssignee[]>([]);
  const [assigneeId, setAssigneeId] = useState(task.assignedTo ?? "");
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const availableActions = nextActions(task, user.role);
  const canUpdateStatus =
    user.role === "SISTEM_YONETICISI" ||
    !task.assignedTo ||
    task.assignedTo === user.id;
  const actions = canUpdateStatus ? availableActions : [];
  const primaryActions = actions.filter(
    (action) => !exceptionStatuses.has(action.status),
  );
  const exceptionActions = actions.filter((action) =>
    exceptionStatuses.has(action.status),
  );
  const quantityNeeded = actions.some((action) =>
    ["PARTIALLY_PREPARED", "READY_FOR_TRANSFER", "PHYSICAL_CONFIRMED"].includes(
      action.status,
    ),
  );
  const physicalConfirmationAvailable = actions.some(
    (action) => action.status === "PHYSICAL_CONFIRMED",
  );
  const stagingConfirmation = task.routeLeg === "VERIFY_STAGING";
  const canManageAssignment =
    user.role === "SISTEM_YONETICISI" || user.role === "PLANLAMA";
  const toggleAssignment = async () => {
    if (assignmentOpen) {
      setAssignmentOpen(false);
      return;
    }
    setAssigneesLoading(true);
    try {
      const result = await apiRequest<{ items: TaskAssignee[] }>(
        `/api/material-preparation/tasks/${task.id}/assignees`,
        { cache: "no-store" },
      );
      setAssignees(result.items);
      setAssigneeId(
        task.assignedTo &&
          result.items.some((item) => item.id === task.assignedTo)
          ? task.assignedTo
          : (result.items.find((item) => item.id === user.id)?.id ??
              result.items[0]?.id ??
              ""),
      );
      setAssignmentOpen(true);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Uygun sorumlular alınamadı.",
      );
    } finally {
      setAssigneesLoading(false);
    }
  };
  const assign = async () => {
    if (!assigneeId) {
      notify("Atanacak sorumluyu seçin.");
      return;
    }
    if (note.trim().length < 3) {
      notify("Atama açıklamasını en az 3 karakter girin.");
      return;
    }
    setSaving(true);
    try {
      const next = await apiRequest<Task>(
        `/api/material-preparation/tasks/${task.id}/assignment`,
        {
          method: "POST",
          body: JSON.stringify({
            assigneeId,
            note: note.trim(),
            expectedVersion: task.version,
          }),
        },
      );
      onSaved(next);
      setNote("");
      setAssignmentOpen(false);
      notify(`${task.materialCode} görevinin sorumlusu güncellendi.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Sorumlu atanamadı.");
    } finally {
      setSaving(false);
    }
  };
  const save = async (status: TaskStatus) => {
    const numeric = quantity === "" ? undefined : Number(quantity);
    if (numeric !== undefined && (!Number.isFinite(numeric) || numeric < 0)) {
      notify("Miktar sıfır veya daha büyük olmalıdır.");
      return;
    }
    setSaving(true);
    try {
      if (status === "PHYSICAL_CONFIRMED") {
        if (numeric === undefined)
          throw new Error("Teyit edilen fiziki miktarı girin.");
        const checkResult = await apiRequest<{
          items: Array<{ id: string; version: number }>;
        }>(
          `/api/material-preparation/checks?runId=${encodeURIComponent(task.runId)}&latest=true&exceptions=false`,
          { cache: "no-store" },
        );
        const check = checkResult.items.find(
          (item) => item.id === task.checkId,
        );
        if (!check)
          throw new Error("Göreve ait güncel malzeme kontrolü bulunamadı.");
        await apiRequest(
          `/api/material-preparation/checks/${task.checkId}/items/${encodeURIComponent(task.materialCode)}/counts`,
          {
            method: "POST",
            body: JSON.stringify({
              scope: "STAGING",
              quantity: numeric,
              unit: task.unit,
              note: note.trim(),
              idempotencyKey: crypto.randomUUID(),
              expectedVersion: check.version,
            }),
          },
        );
        setNote("");
        await onRefresh();
        notify(`${task.materialCode} cep depo fiziki miktarı teyit edildi.`);
        return;
      }
      if (note.trim().length < 3) {
        notify("İşlem açıklamasını en az 3 karakter girin.");
        return;
      }
      const body: {
        status: TaskStatus;
        note: string;
        expectedVersion: number;
        assignToSelf: boolean;
        preparedQuantity?: number;
        receivedQuantity?: number;
      } = {
        status,
        note: note.trim(),
        expectedVersion: task.version,
        assignToSelf: true,
      };
      if (status === "PARTIALLY_PREPARED" || status === "READY_FOR_TRANSFER")
        body.preparedQuantity = numeric;
      onSaved(
        await apiRequest<Task>(
          `/api/material-preparation/tasks/${task.id}/status`,
          { method: "POST", body: JSON.stringify(body) },
        ),
      );
      setNote("");
      notify(`${task.materialCode} görevi güncellendi.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Görev güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };
  return { quantity, setQuantity, note, setNote, saving, assignmentOpen, assignees,
    assigneeId, setAssigneeId, assigneesLoading, availableActions, primaryActions,
    exceptionActions, quantityNeeded, physicalConfirmationAvailable, stagingConfirmation,
    canManageAssignment, toggleAssignment, assign, save };
}
